import { type ReactNode, useId } from "react";

import { cn } from "@/shared/lib/utils";

import { RadioGroup, RadioGroupItem } from "../primitives/radio-group";

/** A single card option in the choice group. */
export type ChoiceCardOption = {
  /** Value submitted with the form. Must match the string expected by the Zod schema. */
  value: string;
  /** Bold title shown inside the card. */
  label: ReactNode;
  /** Secondary text shown below the label. Use for pricing, constraints, or a brief description. */
  description?: ReactNode;
  /** Icon or illustration rendered above the label. Use a `size-5` or `size-6` Lucide icon. */
  icon?: ReactNode;
  disabled?: boolean;
};

export interface ColocaChoiceCardsProps {
  /** Currently selected value. Pass `""` for "nothing selected". */
  value: string;
  onValueChange: (value: string) => void;
  onBlur?: () => void;
  items: ChoiceCardOption[];
  /**
   * Number of columns in the card grid. Defaults to `1`. Use `1` for long descriptions, `3` for short labels
   * with icons.
   */
  columns?: 1 | 2 | 3;
  disabled?: boolean;
  /** Accessible name for the group. Required when no visible label sits above it. */
  "aria-label"?: string;
  "aria-labelledby"?: string;
  className?: string;
}

const COLUMN_CLASS: Record<1 | 2 | 3, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
};

/**
 * Choice card group — a radio group whose options are cards carrying an icon, a title and a description.
 *
 * This is the controlled, form-agnostic half. Inside a `<ColocaForm>` use `ColocaChoiceCardField`, which
 * binds it to React Hook Form and renders label, description and validation message. Reach for this one when
 * the selection is component state rather than a form field — a wizard step that decides which form to show
 * next, for instance.
 */
export function ColocaChoiceCards({
  value,
  onValueChange,
  onBlur,
  items,
  columns = 1,
  disabled,
  className,
  ...aria
}: ColocaChoiceCardsProps) {
  const groupId = useId();

  return (
    <RadioGroup
      value={value}
      // The primitive hands back `unknown` (it accepts any value type); every option here is a string.
      onValueChange={(next) => onValueChange(String(next))}
      onBlur={onBlur}
      disabled={disabled}
      className={cn("grid gap-3", COLUMN_CLASS[columns], className)}
      {...aria}
    >
      {items.map((item) => {
        const itemId = `${groupId}-${item.value}`;
        const isSelected = value === item.value;
        const isItemDisabled = disabled || item.disabled;

        return (
          <label
            key={item.value}
            htmlFor={itemId}
            className={cn(
              "flex flex-col gap-3 rounded-lg border p-4 transition-colors select-none",
              isItemDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
              isSelected
                ? "border-primary bg-primary/5"
                : cn("border-input", !isItemDisabled && "hover:border-primary/40 hover:bg-accent/50"),
            )}
          >
            <div className="flex items-start justify-between gap-2">
              {item.icon ? (
                <div className={cn("text-muted-foreground", isSelected && "text-primary")}>{item.icon}</div>
              ) : (
                <span />
              )}
              {/* The card is the `<label>`, so every gesture — pointer or arrow key — produces exactly ONE
                  selection: the browser forwards it to this radio's hidden input and stops there. Do not give
                  the card an `onClick`. It used to have one, and because Base UI renders that hidden input
                  INSIDE the card, arrow-key navigation (which selects by calling `.click()` on it) bubbled
                  into the handler and ran the consumer's `onValueChange` twice per keystroke.
                  `pointer-events-none` keeps the visual radio from being its own hit target, so a click on it
                  reaches the label like a click anywhere else on the card. */}
              <RadioGroupItem
                id={itemId}
                value={item.value}
                disabled={isItemDisabled}
                className="mt-0.5 shrink-0 pointer-events-none"
              />
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-sm leading-none font-medium">{item.label}</p>
              {item.description && (
                <p className="text-xs leading-snug text-muted-foreground">{item.description}</p>
              )}
            </div>
          </label>
        );
      })}
    </RadioGroup>
  );
}
